import { useState, useEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { Html5Qrcode } from 'html5-qrcode';
import * as api from '../../services/api';

const SCANNER_ID = 'gotaps-card-scanner';

// Pull a card UUID out of whatever the QR contained.
// Accepts:  https://gotaps.me/card_id/abc-123   →  abc-123
//           card_id/abc-123                      →  abc-123
//           abc-123                              →  abc-123 (raw uuid fallback)
// Returns null if it doesn't look like a GoTaps card QR.
export function parseCardUuid(text) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();

  // Path-based form: anything containing "card_id/<uuid>"
  const pathMatch = trimmed.match(/card_id\/([A-Za-z0-9_-]+)/i);
  if (pathMatch) return pathMatch[1];

  // Raw uuid fallback — must look uuid-ish (letters/digits/dashes, no slashes)
  if (/^[A-Za-z0-9_-]{6,}$/.test(trimmed)) return trimmed;

  return null;
}

export const ModalScanCard = memo(function ModalScanCard({
  profileId,
  expectedType,
  onClose,
  onAssigned,
}) {
  const [phase, setPhase] = useState('init');   // init | scanning | error
  const [error, setError] = useState(null);     // { message, reason }
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const handlingRef = useRef(false);            // guards against double-fire while await is pending

  // Try to assign the card given a UUID. On success, hand the card to the
  // parent (for toast + list refresh) and close immediately. The card row
  // showing up in Active Devices is the user's confirmation.
  const tryAssign = useCallback(async (uuid) => {
    if (handlingRef.current) return;
    handlingRef.current = true;
    try {
      const card = await api.assignCard(profileId, { uuid, expected_type: expectedType });
      onAssigned?.(card);
      onClose?.();
    } catch (err) {
      setError({
        message: err.message || 'Failed to assign card',
        reason: err.data?.reason || 'unknown',
      });
      setPhase('error');
    } finally {
      handlingRef.current = false;
    }
  }, [profileId, expectedType, onAssigned, onClose]);

  // Stop the live camera. Safe to call repeatedly.
  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      await s.clear();
    } catch { /* ignore — already stopped */ }
    scannerRef.current = null;
  }, []);

  const startScanner = useCallback(async () => {
    setError(null);
    setPhase('scanning');

    // Defer one tick so the scanner DOM node is mounted before Html5Qrcode reads it.
    await new Promise((r) => setTimeout(r, 50));

    try {
      const scanner = new Html5Qrcode(SCANNER_ID, /* verbose */ false);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        async (decoded) => {
          const uuid = parseCardUuid(decoded);
          if (!uuid) {
            // Wrong kind of QR — keep scanning, don't pop an error each frame.
            return;
          }
          await stopScanner();
          await tryAssign(uuid);
        },
        () => { /* scan attempt failures are continuous; ignore */ },
      );
    } catch (err) {
      setError({
        message: err?.message || 'Could not access the camera. You can upload a photo instead.',
        reason: 'camera_unavailable',
      });
      setPhase('error');
    }
  }, [stopScanner, tryAssign]);

  // Auto-start the camera when modal opens.
  useEffect(() => { startScanner(); /* eslint-disable-line */ }, []);

  // Cleanup on unmount.
  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  // Esc to close (unless we're mid-async-call)
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await stopScanner();
    setPhase('scanning');
    setError(null);
    try {
      const scanner = new Html5Qrcode(SCANNER_ID, false);
      const decoded = await scanner.scanFile(file, /* showImage */ false);
      const uuid = parseCardUuid(decoded);
      if (!uuid) {
        setError({
          message: 'That image does not contain a valid GoTaps card QR.',
          reason: 'parse_failed',
        });
        setPhase('error');
        return;
      }
      await tryAssign(uuid);
    } catch {
      setError({
        message: 'Could not read a QR from that image. Try a clearer photo.',
        reason: 'parse_failed',
      });
      setPhase('error');
    }
  };

  const retry = () => {
    setError(null);
    handlingRef.current = false;
    startScanner();
  };

  return createPortal(
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        width: '100%', maxWidth: 460,
        background: 'rgba(255,255,255,.98)', backdropFilter: 'blur(40px)',
        WebkitBackdropFilter: 'blur(40px)',
        borderRadius: 22,
        boxShadow: '0 32px 80px rgba(0,0,0,.18), 0 8px 32px rgba(0,0,0,.1)',
        overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 22px 14px',
          borderBottom: '1px solid rgba(0,0,0,.06)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'rgba(8,145,178,.1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 19,
            }}>📷</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 720, color: '#0f172a', letterSpacing: '-.2px' }}>
                Scan {expectedType} QR
              </div>
              <div style={{ fontSize: 11.5, color: '#64748b', marginTop: 2 }}>
                Hold the card's QR code in front of the camera.
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: '50%',
              border: 'none', background: 'rgba(0,0,0,.07)',
              cursor: 'pointer', fontSize: 14, color: '#64748b',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >✕</button>
        </div>

        {/* Body — scanner / error / success */}
        <div style={{ padding: '18px 22px', minHeight: 320 }}>
          {/* Always-mounted scanner DOM; we just hide it when we're not scanning. */}
          <div
            id={SCANNER_ID}
            style={{
              display: phase === 'scanning' ? 'block' : 'none',
              width: '100%', borderRadius: 14, overflow: 'hidden',
              background: '#0f172a',
            }}
          />

          {phase === 'error' && error && (
            <div style={{ padding: '8px 0' }}>
              <div style={{
                padding: '14px 16px', borderRadius: 12,
                background: 'rgba(220,38,38,.06)',
                border: '1px solid rgba(220,38,38,.2)',
                color: '#b91c1c',
                fontSize: 13, lineHeight: 1.5,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>⚠ Cannot assign this card</div>
                <div>{error.message}</div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 22px',
          borderTop: '1px solid rgba(0,0,0,.06)',
          background: 'rgba(248,250,252,.7)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
        }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={handleFile}
          />
          {phase === 'scanning' && (
            <button
              className="btn bs"
              style={{ fontSize: 12 }}
              onClick={() => fileInputRef.current?.click()}
            >📁 Use a photo instead</button>
          )}
          {phase === 'error' && (
            <button
              className="btn bs"
              style={{ fontSize: 12 }}
              onClick={retry}
            >↻ Try again</button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn bp" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body,
  );
});
